import { forwardRef, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';

import { type Control, type FormKey, type Values } from './RelationDivisionLine.types';
import { type ValueOf } from '~/common/types';

import CONSTANTS from './RelationDivisionLine.constants';
import { DivisionType } from '~/common/enums';

import {
  getPercentageTotal,
  type ToUpdateShareValues,
  updateShareAt,
  withDerivedValues,
} from '../RelationDivisionLines.utils';
import { toDivisionPercentage } from './RelationDivisionLine.utils';

import { useDivisionValues, useParent } from '../RelationDivisionLines.hooks';
import useEvent from '@/shared/hooks/useEvent';

import RelationDivisionLine from './RelationDivisionLine';

const DISPLAY_NAME = 'FormikRelationDivisionLine';

const validateNumberValue = (value: ValueOf<Values>) => {
  const isNumberOrNullish = value == null || typeof value === 'number';

  const outputFactories = {
    invalid: () =>
      ({
        error: new Error('Type mismatch: expected number or null'),
        isValid: false,
        value: null,
      } as const),
    valid: (toOutputValue: number | null) =>
      ({
        error: null,
        isValid: true,
        value: toOutputValue,
      } as const),
  };

  return isNumberOrNullish ? outputFactories.valid(value) : outputFactories.invalid();
};

const createNumberHandler =
  (handler: (value: number | null) => void) => (value: ValueOf<Values>) => {
    const { error, isValid, value: numberValue } = validateNumberValue(value);

    if (!isValid) throw error;

    handler(numberValue);
  };

type Props = {
  index: number;
  totalAmount: number;
};

const FormikRelationDivisionLine = forwardRef<HTMLLIElement, Props>(
  ({ index, totalAmount }, ref) => {
    const { divisionType, divisions } = useDivisionValues();

    const formik = useFormikContext();
    const parent = useParent();

    const path = `${parent.name}[${index}]`;
    const share = divisions[index];

    const getError = (key: FormKey): string | null => {
      const fieldPath = `${path}.${key}`;

      return getIn(formik.touched, fieldPath) ? getIn(formik.errors, fieldPath) ?? null : null;
    };

    const updateValuesForCurrentDivision = (values: ToUpdateShareValues) => {
      const nextDivisions = updateShareAt(divisions, index, values);

      return formik.setFieldValue(parent.name, nextDivisions);
    };

    const handleAmountChange = createNumberHandler((value: number | null) => {
      const nextAmount = value ?? 0;
      const nextPercentage = totalAmount ? toDivisionPercentage(nextAmount / totalAmount) : 0;

      updateValuesForCurrentDivision({ amount: nextAmount, percentage: nextPercentage });
    });

    const handlePercentageChange = createNumberHandler((value: number | null) => {
      const nextPercentage = value ?? 0;
      const nextShares = updateShareAt(divisions, index, {
        amount: null,
        percentage: nextPercentage,
      });

      const nextSharesWithSpreadRounding = withDerivedValues({
        divisionType: DivisionType.Percentage,
        shares: nextShares,
        totalAmount,
      });

      formik.setFieldValue(parent.name, nextSharesWithSpreadRounding);
    });

    const handleChange: Control['handleChange'] = useEvent((key, value) => {
      const valueName = `${path}.${key}`;

      formik.setFieldTouched(valueName, true, false);

      switch (key) {
        case CONSTANTS.FORM_KEYS.AMOUNT:
          handleAmountChange(value);

          break;
        case CONSTANTS.FORM_KEYS.PERCENTAGE:
          handlePercentageChange(value);

          break;
        default:
          formik.setFieldValue(valueName, value);
      }
    });

    const handleRemove = useEvent(() => {
      const nextDivisions = divisions.filter((_, divisionIndex) => divisionIndex !== index);

      formik.setFieldValue(parent.name, nextDivisions);
    });

    const amount = share?.amount ?? null;
    const percentage = share?.percentage ?? null;
    const buildingRelationId = share?.buildingRelationId ?? null;
    const unitId = share?.unitId ?? null;

    const amountError = getError(CONSTANTS.FORM_KEYS.AMOUNT);
    const percentageError = getError(CONSTANTS.FORM_KEYS.PERCENTAGE);
    const relationError = getError(CONSTANTS.FORM_KEYS.RELATION);
    const unitError = getError(CONSTANTS.FORM_KEYS.UNIT);

    const control = useMemo<Control>(
      () => ({
        errors: {
          [CONSTANTS.FORM_KEYS.AMOUNT]: amountError,
          [CONSTANTS.FORM_KEYS.PERCENTAGE]: percentageError,
          [CONSTANTS.FORM_KEYS.RELATION]: relationError,
          [CONSTANTS.FORM_KEYS.UNIT]: unitError,
        },
        handleChange,
        values: {
          [CONSTANTS.FORM_KEYS.AMOUNT]: amount,
          [CONSTANTS.FORM_KEYS.PERCENTAGE]: percentage,
          [CONSTANTS.FORM_KEYS.RELATION]: buildingRelationId,
          [CONSTANTS.FORM_KEYS.UNIT]: unitId,
        },
      }),
      [
        amount,
        amountError,
        buildingRelationId,
        handleChange,
        percentage,
        percentageError,
        relationError,
        unitError,
        unitId,
      ],
    );

    const maxPercentage =
      100 - getPercentageTotal(divisions, (_, divisionIndex) => divisionIndex !== index);

    return (
      <RelationDivisionLine
        ref={ref}
        control={control}
        divisionType={divisionType}
        maxPercentage={maxPercentage}
        onRemove={handleRemove}
      />
    );
  },
);

FormikRelationDivisionLine.displayName = DISPLAY_NAME;

export default FormikRelationDivisionLine;
